import type {
  FileEntryCondition,
  FormColumn,
  FormRow,
  FormSection,
  SourceFieldRef,
  WorkflowField,
} from '@/api/workflows';

/**
 * Front-end mirror of the back-end's canonical `ConditionStripper` (byte-for-byte
 * algorithm match — see apps/back-end/.../Domain/CustomElements/ConditionStripper.cs).
 * Runs client-side purely for immediate UX (stripping before the save-as-custom POST);
 * the BE re-runs the same logic server-side as defense-in-depth and never trusts this
 * output. All functions are pure — they return new objects, never mutate the input.
 *
 * Rules:
 *   1. An element's own top-level gating condition is always dropped.
 *   2. A nested condition list (field-inside-section, section/field-inside-step)
 *      survives only if EVERY condition's fieldId resolves to a field also inside
 *      the same saved subtree — otherwise the WHOLE list is cleared, never partially.
 *   3. At step scope, a surviving condition must ALSO match fieldStepId against the
 *      step being saved (a FormSection carries no step id, so section scope only
 *      checks fieldId membership).
 *   4. A field's `sourceField` (cross-step prefill reference) follows the same
 *      membership rule as a condition: it survives only if the referenced field is
 *      also inside the saved subtree (and, at step scope, points at the step being
 *      saved); otherwise it's cleared. Evaluated INDEPENDENTLY of the field's
 *      conditions — one can survive while the other is cleared.
 */

function allReferencesWithin(
  conditions: FileEntryCondition[],
  fieldIds: ReadonlySet<string>,
  sourceStepId?: string,
): boolean {
  if (conditions.length === 0) return true;
  return conditions.every((c) => {
    if (!fieldIds.has(c.fieldId)) return false;
    if (sourceStepId !== undefined && c.fieldStepId !== sourceStepId) return false;
    return true;
  });
}

/** True when the ref is absent (nothing to strip) or its target field lives inside
 *  the saved subtree — and, at step scope, points at the step being saved. Mirrors
 *  {@link allReferencesWithin} for a single reference. */
function sourceFieldWithin(
  ref: SourceFieldRef | null | undefined,
  fieldIds: ReadonlySet<string>,
  sourceStepId?: string,
): boolean {
  if (!ref) return true;
  if (!fieldIds.has(ref.fieldId)) return false;
  if (sourceStepId !== undefined && ref.stepId !== sourceStepId) return false;
  return true;
}

function clearedFieldConditions(field: WorkflowField): WorkflowField {
  return { ...field, conditions: [], conditionCombinator: null };
}

/** Clears a field's cross-references (conditions + sourceField) independently: each
 *  survives only if it resolves inside the saved subtree. */
function sanitizeFieldReferences(
  field: WorkflowField,
  fieldIds: ReadonlySet<string>,
  sourceStepId?: string,
): WorkflowField {
  let result = field;
  if (!allReferencesWithin(field.conditions, fieldIds, sourceStepId)) {
    result = clearedFieldConditions(result);
  }
  if (!sourceFieldWithin(field.sourceField, fieldIds, sourceStepId)) {
    result = { ...result, sourceField: null };
  }
  return result;
}

function clearedSection(section: FormSection): FormSection {
  return { ...section, conditions: [], conditionCombinator: null };
}

function collectFieldIds(sections: FormSection[]): Set<string> {
  const ids = new Set<string>();
  for (const section of sections) {
    for (const row of section.rows) {
      for (const column of row.columns) {
        if (column.field) ids.add(column.field.id);
      }
    }
  }
  return ids;
}

function stripFieldsInRows(
  rows: FormRow[],
  fieldIds: ReadonlySet<string>,
  sourceStepId?: string,
): FormRow[] {
  return rows.map((row) => ({
    ...row,
    columns: row.columns.map((column): FormColumn => {
      if (!column.field) return column;
      return { ...column, field: sanitizeFieldReferences(column.field, fieldIds, sourceStepId) };
    }),
  }));
}

/** Strips a field's own cross-references. A standalone field has no subtree, so any
 *  visibility condition OR sourceField reference points outside the saved element
 *  and is always cleared. */
export function stripFieldForCustomElement(field: WorkflowField): WorkflowField {
  return { ...clearedFieldConditions(field), sourceField: null };
}

/**
 * Strips a section's own visibility condition, plus clears any nested field
 * condition that references a field outside the section (whole list, not partial).
 */
export function stripSectionForCustomElement(section: FormSection): FormSection {
  const fieldIds = collectFieldIds([section]);
  const rows = stripFieldsInRows(section.rows, fieldIds);
  return clearedSection({ ...section, rows });
}

/**
 * Strips conditions one level up: every section/field inside the given step's
 * sections keeps its condition only if it references a field inside the SAME
 * step AND (for sections) the condition's fieldStepId matches sourceStepId.
 * A step itself carries no top-level visibility condition to clear (mirrors
 * CustomStepContent having no Conditions property on the back end).
 */
export function stripStepForCustomElement(
  sections: FormSection[],
  sourceStepId: string,
): FormSection[] {
  const fieldIds = collectFieldIds(sections);
  return sections.map((section) => {
    const withClearedOwnCondition = allReferencesWithin(section.conditions, fieldIds, sourceStepId)
      ? section
      : clearedSection(section);
    return {
      ...withClearedOwnCondition,
      rows: stripFieldsInRows(withClearedOwnCondition.rows, fieldIds, sourceStepId),
    };
  });
}
